import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fixture, KickoffCell, MatchFlags, StatusPill } from '../components/match';
import { Topbar } from '../components/Shell';
import { Empty, ErrorBox, Loading } from '../components/ui';
import { get } from '../lib/api';
import { fmtDate } from '../lib/format';
import type { MatchRow } from '../lib/types';

interface Dash {
  counts: Record<string, number>;
  round: { id: number; name: string; startsAt: string; endsAt: string } | null;
  live: MatchRow[];
  roundMatches: MatchRow[];
}

export function DashboardPage() {
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => get<Dash>('/admin/dashboard'), refetchInterval: 15_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const d = q.data!;
  const c = d.counts;
  const tiles: [string, number, string, boolean?][] = [
    ['Players', c.users ?? 0, '/users'],
    ['Verified', c.verified ?? 0, '/users'],
    ['New this week', c.newUsers ?? 0, '/users'],
    ['Predictions today', c.predictionsToday ?? 0, '/matches'],
    ['Private leagues', c.leagues ?? 0, '/leagues'],
    ['Live now', d.live.length, '/matches'],
    ['Open data issues', c.openIssues ?? 0, '/issues', (c.openIssues ?? 0) > 0],
    ['Scoring behind', c.needsRescore ?? 0, '/boards', (c.needsRescore ?? 0) > 0],
    ['Drafts to publish', c.unpublished ?? 0, '/slate'],
    ['Awaiting confirmation', c.unsettled ?? 0, '/matches'],
  ];
  return (
    <>
      <Topbar
        title="Dashboard"
        sub={d.round ? `${d.round.name} · ${fmtDate(d.round.startsAt)} to ${fmtDate(d.round.endsAt)}` : 'No round with published matches yet'}
        actions={
          <Link to="/matches/new" className="btn btn-primary">
            New match
          </Link>
        }
      />
      <div className="page">
        <div className="kpis">
          {tiles.map(([l, v, to, warn]) => (
            <Link key={l} to={to} className={`kpi${warn ? ' warn' : ''}`}>
              <span className="v num">{v.toLocaleString()}</span>
              <span className="l">{l}</span>
            </Link>
          ))}
        </div>

        {d.live.length > 0 && (
          <section className="card" aria-labelledby="live-h">
            <div className="card-head">
              <h2 id="live-h">Live now</h2>
            </div>
            <table className="table">
              <tbody>
                {d.live.map((m) => (
                  <tr key={m.id}>
                    <td style={{ width: 90 }}>
                      <StatusPill m={m} />
                    </td>
                    <td className="muted">{m.competitionName}</td>
                    <td style={{ minWidth: 320 }}>
                      <Fixture m={m} />
                    </td>
                    <td className="right num">{m.predictionCount.toLocaleString()} picks</td>
                    <td className="right">
                      <Link to="/matches/$matchId" params={{ matchId: String(m.id) }} className="btn btn-sm btn-primary">
                        Control room
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <section className="card" aria-labelledby="round-h">
          <div className="card-head">
            <h2 id="round-h">{d.round?.name ?? 'This week'}</h2>
            <div className="actions">
              <Link to="/slate" className="btn btn-sm">
                Open slate
              </Link>
            </div>
          </div>
          {d.roundMatches.length === 0 ? (
            <Empty>Nothing scheduled.</Empty>
          ) : (
            <table className="table">
              <tbody>
                {d.roundMatches.map((m) => (
                  <tr key={m.id}>
                    <td style={{ width: 170 }}>
                      <KickoffCell m={m} />
                    </td>
                    <td className="muted">{m.competitionName}</td>
                    <td style={{ minWidth: 320 }}>
                      <Fixture m={m} />
                    </td>
                    <td>
                      <StatusPill m={m} />
                    </td>
                    <td className="right num">{m.predictionCount.toLocaleString()} picks</td>
                    <td className="right">
                      <MatchFlags m={m} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}
